import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { omit } from 'lodash-es';
import { useQuery } from 'react-query';

import {
  selectAvailabilityTableState,
  filtersModalConditionAvailabilityChange,
  resetModalAvailabilityChange,
  AVAILABILITY_PAGE_SIZE,
  filtersModalRulesAvailabilityChange,
} from '@store';
import { showError, getCleanedUrlParams } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';

import { availabilityServices } from '@services';
import {
  getAvailabilityQueryParams,
  getAvailabilityRequestPayload,
  handleAvailability,
  handleEnums,
} from './utils';

export const useAvailabilityTableOperations = () => {
  const { filters } = useSelector(selectAvailabilityTableState);

  const dispatch = useDispatch();

  const { query, isReady } = useRouter();
  const {
    page = 1,
    pageSize = AVAILABILITY_PAGE_SIZE,
    orderBy = '',
    order = '',
    ...remainingQueries
  } = query;

  const cleanParams = getCleanedUrlParams({
    ...remainingQueries,
    page,
    orderBy,
    order,
    pageSize: parseInt(pageSize, 10) || AVAILABILITY_PAGE_SIZE,
  });

  const {
    data: availabilityEnumsData,
    isLoading: isAvailabilityEnumsLoading,
    isError: isAvailabilityEnumsError,
    error: availabilityEnumsError,
    isFetched: isAvailabilityEnumsIsFetched,
  } = useQuery(
    [QUERY_KEYS.GET_AVAILABILITY_ENUMS_KEY],
    () => availabilityServices?.getAvailabilityEnums(),
    {
      enabled: !!isReady,
    }
  );

  const enums = handleEnums(availabilityEnumsData);

  const {
    data: availabilityData,
    isLoading: isAvailabilityLoading,
    isError: isAvailabilityError,
    error: availabilityError,
    refetch: refetchAvailability,
  } = useQuery(
    [
      QUERY_KEYS.GET_AVAILABILITY_KEY,
      { cleanParams, filters: filters.modalFiltersRules },
    ],
    () => {
      const params = getAvailabilityQueryParams(cleanParams);
      const payload = getAvailabilityRequestPayload(
        omit(cleanParams, ['query']),
        filters.modalFiltersRules,
        enums
      );

      if (params.tab === 'predel' && !params.order) {
        params.orderBy = 'vehicleMileageFromOdometer';
        params.order = 'desc';
      }

      return availabilityServices?.getAvailability({
        params,
        payload,
      });
    },
    {
      enabled: !!isReady && isAvailabilityEnumsIsFetched,
      refetchOnMount: false,
    }
  );

  const data = handleAvailability(availabilityData?.data || []);

  const total = availabilityData?.meta?.total;

  const updateFilterConditions = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionAvailabilityChange(conditions));
    },
    [dispatch]
  );

  const resetModalChange = useCallback(() => {
    dispatch(resetModalAvailabilityChange());
  }, [dispatch]);

  const updateFilterRules = (rules) => {
    dispatch(filtersModalRulesAvailabilityChange(rules));
  };

  useEffect(() => {
    if (isAvailabilityError) {
      showError(availabilityError);
    }
  }, [isAvailabilityError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAvailabilityEnumsError) {
      showError(availabilityEnumsError);
    }
  }, [isAvailabilityEnumsError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = isAvailabilityLoading || isAvailabilityEnumsLoading;
  const enumsLoading = isAvailabilityEnumsLoading;

  return {
    loading,
    total,
    data,
    page,
    pageSize: parseInt(pageSize, 10) || AVAILABILITY_PAGE_SIZE,
    enums,
    enumsLoading,
    filters,
    refetchAvailability,
    updateFilterConditions,
    updateFilterRules,
    resetModalChange,
  };
};
